import { useEffect, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { safeNum } from '../lib/format'
import type { WorkOrder } from '@shared/types'
import { useAuthStore } from '../store/auth'
import { StatsSkeleton } from '../components/ui/Skeletons'
import { StatCard } from '../components/ui/StatCard'
import { GlowCard } from '../components/ui/GlowCard'
import { Badge } from '../components/ui/Badge'

interface DashboardStatsData {
  pipeline: { value: number; count: number }
  openWorkOrders: { count: number; urgent: number }
  activeSites: { count: number; devices: number }
}

interface ActivityItem {
  id: string
  action: string
  entity_type: string
  entity_name: string
  created_at: string
  user_name?: string
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'var(--err)',
  high: 'var(--accent)',
  normal: 'var(--warn)',
  low: 'var(--ok)',
}

// Icon components for stat cards
function DollarIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
}

function FileTextIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
    </svg>
  )
}

function MonitorIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  )
}

function TicketIcon({ size = 17, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    </svg>
  )
}

function getStatusBadge(status: string) {
  const map: Record<string, 'ok' | 'warn' | 'err' | 'info' | 'default'> = {
    new: 'ok', scheduled: 'info', dispatched: 'err', in_progress: 'warn',
    completed: 'ok', cancelled: 'err',
  }
  return <Badge v={map[status] || 'default'} sm>{status.replace('_', ' ')}</Badge>
}

export default function Dashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStatsData | null>(null)
  const [schedule, setSchedule] = useState<WorkOrder[]>([])
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [showSetupCard, setShowSetupCard] = useState(false)
  const [setupStep, setSetupStep] = useState(1)
  const [mrr, setMrr] = useState<number | null>(null)

  useEffect(() => {
    async function load() {
      try {
        const [statsData, scheduleData, activityData] = await Promise.all([
          api.get<DashboardStatsData>('/dashboard').catch(() => null),
          api.get<WorkOrder[]>('/dispatch/today').catch(() => []),
          api.get<ActivityItem[]>('/activity-log?limit=10').catch(() => []),
        ])
        if (statsData) setStats(statsData)
        setSchedule(Array.isArray(scheduleData) ? scheduleData : [])
        setActivity(Array.isArray(activityData) ? activityData : [])
      } catch { /* handled */ }
      finally { setLoading(false) }
    }
    load()

    // Load MRR
    api.get<{ mrr: number }>('/rmr/dashboard')
      .then(r => setMrr(r.mrr))
      .catch(() => {})

    // Check onboarding status
    api.get<{ completed: number; skipped: number; current_step: number }>('/onboarding')
      .then(progress => {
        if (!progress.completed && !progress.skipped) {
          setShowSetupCard(true)
          setSetupStep(progress.current_step || 1)
        }
      })
      .catch(() => {})
  }, [])

  if (loading) return <StatsSkeleton count={3} />

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
  const firstName = user?.full_name?.split(' ')[0] || 'there'

  return (
    <div style={{ padding: `calc(32px * var(--density))`, maxWidth: 1200 }}>

      {/* Greeting */}
      <div style={{ marginBottom: 8 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-1)', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {greeting},{' '}
          <span style={{
            background: 'linear-gradient(135deg, var(--accent), var(--accent-h))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            {firstName}
          </span>
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4 }}>
          <span style={{ fontFamily: 'var(--mono)' }}>{dateStr}</span>
          {stats?.pipeline.count ? ` · ${stats.pipeline.count} proposals in pipeline` : ''}
        </p>
      </div>

      {/* Onboarding Setup Card */}
      {showSetupCard && (
        <GlowCard style={{ padding: '20px 24px', marginBottom: 24, marginTop: 16 }} delay={50}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
            background: 'linear-gradient(135deg, var(--accent-m), transparent)',
            margin: -20, padding: '20px 24px', borderRadius: 'var(--r-l)',
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4 }}>
                Complete Your Setup
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                You&apos;re on step {setupStep} of 7. Finish setting up to unlock all features.
              </div>
            </div>
            <button
              onClick={() => navigate('/onboarding')}
              style={{
                flexShrink: 0, padding: '8px 20px', borderRadius: 'var(--r-m)',
                background: 'var(--accent)', color: 'var(--accent-t)',
                border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              Continue Setup
            </button>
          </div>
        </GlowCard>
      )}

      {/* Stat Cards */}
      <div style={{ display: 'flex', gap: `calc(14px * var(--density))`, marginTop: 24 }}>
        <StatCard
          icon={DollarIcon}
          label="Pipeline Value"
          value={safeNum(stats?.pipeline.value)}
          prefix="$"
          trendVal={stats?.pipeline.count ? `${stats.pipeline.count} proposals` : undefined}
          spark={[120, 135, 142, 138, 155, 169]}
          delay={100}
        />
        <StatCard
          icon={FileTextIcon}
          label="Open Work Orders"
          value={safeNum(stats?.openWorkOrders.count)}
          trendVal={stats?.openWorkOrders.urgent ? `${stats.openWorkOrders.urgent} urgent` : undefined}
          trend={stats?.openWorkOrders.urgent ? 'down' : undefined}
          spark={[5, 3, 7, 4, 6, 2]}
          delay={200}
        />
        <StatCard
          icon={MonitorIcon}
          label="Active Sites"
          value={safeNum(stats?.activeSites.count)}
          trendVal={`${safeNum(stats?.activeSites.devices)} devices`}
          trend="up"
          spark={[18, 20, 19, 22, 21, 23]}
          delay={300}
        />
        {mrr !== null && (
          <StatCard
            icon={DollarIcon}
            label="Monthly Recurring"
            value={safeNum(Math.round(safeNum(mrr)))}
            prefix="$"
            trend="up"
            spark={[6200, 6800, 7100, 7400, 7900, 8200]}
            delay={400}
          />
        )}
      </div>

      {/* Quick Actions */}
      <GlowCard style={{ padding: `calc(20px * var(--density))`, marginTop: 24 }} delay={450}>
        <div style={{
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
          color: 'var(--text-3)', marginBottom: 14,
        }}>
          Quick Actions
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
          {[
            { label: 'New Proposal', icon: '📄', path: '/proposals?action=new' },
            { label: 'New Work Order', icon: '🚚', path: '/dispatch?action=new' },
            { label: 'New Client', icon: '👤', path: '/clients?action=new' },
            { label: 'View Reports', icon: '📊', path: '/invoices' },
          ].map((action, i) => (
            <Link
              key={action.label}
              to={action.path}
              className="quick-action"
              style={{
                textDecoration: 'none',
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 12px', borderRadius: 'var(--r-m)',
                border: '1px solid var(--border)', background: 'transparent',
                fontSize: 12, fontWeight: 500, color: 'var(--text-2)',
                transition: 'all var(--dur-f)',
                animation: `in-up 400ms var(--ease) ${(i * 60) + 450}ms both`,
              }}
            >
              <span>{action.icon}</span>
              <span>{action.label}</span>
            </Link>
          ))}
        </div>
      </GlowCard>

      {/* Revenue Chart */}
      <GlowCard style={{ padding: `calc(20px * var(--density))`, marginTop: 24 }} delay={550}>
        <div style={{
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
          color: 'var(--text-3)', marginBottom: 14,
        }}>
          Revenue Overview
        </div>
        <RevenueChart />
      </GlowCard>

      {/* 2-column grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: `calc(18px * var(--density))`, marginTop: 24 }}>

        {/* Left: Today's Schedule */}
        <GlowCard style={{ padding: `calc(20px * var(--density))` }} delay={600}>
          <div style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: 'var(--text-3)', marginBottom: 14,
          }}>
            Today&apos;s Schedule
          </div>
          {schedule.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)', padding: '12px 0' }}>Clear schedule — enjoy the downtime.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {schedule.map((wo, i) => (
                <Link
                  key={wo.id}
                  to={`/dispatch/${wo.slug}`}
                  style={{
                    textDecoration: 'none',
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 'var(--r-m)',
                    transition: 'background var(--dur-f)',
                    animation: `in-right 400ms var(--ease) ${i * 60}ms both`,
                  }}
                  className="schedule-row"
                >
                  <span style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text-3)', minWidth: 68 }}>
                    {wo.scheduled_start ? formatScheduleTime(wo.scheduled_start) : '—'}
                  </span>
                  <span style={{
                    width: 8, height: 8, borderRadius: 4, flexShrink: 0,
                    background: PRIORITY_COLORS[wo.priority] || 'var(--text-3)',
                  }} />
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{wo.title}</span>
                  {getStatusBadge(wo.status)}
                </Link>
              ))}
            </div>
          )}
        </GlowCard>

        {/* Right: Recent Activity */}
        <GlowCard style={{ padding: `calc(20px * var(--density))` }} delay={680}>
          <div style={{
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em',
            color: 'var(--text-3)', marginBottom: 14,
          }}>
            Recent Activity
          </div>
          {activity.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)', padding: '12px 0' }}>All quiet — your team is caught up.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activity.slice(0, 6).map((item, i) => (
                <div
                  key={item.id || i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0',
                    borderBottom: i < 5 ? '1px solid var(--border)' : 'none',
                    animation: `in-right 400ms var(--ease) ${i * 60}ms both`,
                  }}
                >
                  <span style={{
                    width: 30, height: 30, borderRadius: 'var(--r-f)', flexShrink: 0,
                    background: getActivityColor(item.action),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12,
                  }}>
                    {getActivityIcon(item.action)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.user_name && <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{item.user_name} </span>}
                      {item.action} {item.entity_type}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-3)', flexShrink: 0 }}>
                    {formatRelativeTime(item.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </GlowCard>
      </div>

      <style>{`
        .schedule-row:hover { background: var(--bg-3) !important; }
        .quick-action:hover {
          border-color: var(--accent) !important;
          color: var(--text-1) !important;
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  )
}

function formatScheduleTime(t: string): string {
  try {
    return new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  } catch { return t }
}

function formatRelativeTime(dt: string): string {
  try {
    const parsed = new Date(dt.includes('T') ? dt : dt.replace(' ', 'T'))
    const ts = parsed.getTime()
    if (isNaN(ts)) return ''
    const diff = Date.now() - ts
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    const days = Math.floor(hrs / 24)
    if (days === 1) return 'yesterday'
    return `${days}d ago`
  } catch { return '' }
}

function getActivityIcon(action: string): string {
  if (action.includes('complet')) return '✓'
  if (action.includes('creat') || action.includes('add')) return '+'
  if (action.includes('sent') || action.includes('send')) return '→'
  if (action.includes('fetch') || action.includes('sync')) return '⟳'
  if (action.includes('update') || action.includes('edit')) return '✎'
  if (action.includes('delet')) return '✕'
  return '•'
}

function getActivityColor(action: string): string {
  if (action.includes('complet')) return 'var(--ok-m)'
  if (action.includes('creat') || action.includes('add')) return 'var(--accent-m)'
  if (action.includes('sent') || action.includes('send')) return 'var(--info-m)'
  if (action.includes('delet')) return 'var(--err-m)'
  return 'var(--bg-3)'
}

const DEMO_REVENUE = [
  { month: 'Oct', revenue: 12400 },
  { month: 'Nov', revenue: 15200 },
  { month: 'Dec', revenue: 14800 },
  { month: 'Jan', revenue: 18600 },
  { month: 'Feb', revenue: 17200 },
  { month: 'Mar', revenue: 21000 },
]

function RevenueChart() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const w = 600, h = 200, px = 40, py = 16
  const max = Math.max(...DEMO_REVENUE.map(d => d.revenue))
  const min = Math.min(...DEMO_REVENUE.map(d => d.revenue))
  const range = max - min || 1
  const points = DEMO_REVENUE.map((d, i) => ({
    x: px + (i / (DEMO_REVENUE.length - 1)) * (w - px * 2),
    y: py + (1 - (d.revenue - min) / range) * (h - py * 2 - 20),
  }))

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
  const area = `${line} L${points[points.length - 1].x},${h - py - 20} L${points[0].x},${h - py - 20} Z`

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', cursor: 'crosshair' }}>
      <defs>
        <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.15} />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
        </linearGradient>
      </defs>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map(pct => {
        const y = py + pct * (h - py * 2 - 20)
        return <line key={pct} x1={px} y1={y} x2={w - px} y2={y} stroke="rgba(255,255,255,.03)" strokeWidth={1} />
      })}
      {/* Area */}
      <path d={area} fill="url(#rev-grad)" />
      {/* Line */}
      <path d={line} fill="none" stroke="#fbbf24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {/* Hover vertical line */}
      {hoveredIdx !== null && (
        <line x1={points[hoveredIdx].x} y1={py} x2={points[hoveredIdx].x} y2={h - py - 20} stroke="var(--accent)" strokeWidth={1} strokeDasharray="3 3" opacity={0.4} />
      )}
      {/* Interactive data points */}
      {DEMO_REVENUE.map((d, i) => (
        <g key={`pt-${d.month}`} onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
          <rect x={points[i].x - 30} y={py} width={60} height={h - py * 2} fill="transparent" />
          <circle cx={points[i].x} cy={points[i].y} r={hoveredIdx === i ? 6 : 3} fill={hoveredIdx === i ? '#fbbf24' : '#0f0f17'} stroke="#fbbf24" strokeWidth={2} style={{ transition: 'r 150ms' }} />
          {hoveredIdx === i && (
            <>
              <rect x={points[i].x - 44} y={points[i].y - 34} width={88} height={24} rx={6} fill="rgba(15,15,23,0.92)" stroke="var(--accent)" strokeWidth={1} />
              <text x={points[i].x} y={points[i].y - 18} textAnchor="middle" fill="#fbbf24" fontSize={12} fontWeight={600} fontFamily="var(--mono)">
                ${d.revenue.toLocaleString()}
              </text>
            </>
          )}
        </g>
      ))}
      {/* Month labels */}
      {DEMO_REVENUE.map((d, i) => (
        <text key={d.month} x={points[i].x} y={h - 2} textAnchor="middle" fill={hoveredIdx === i ? 'var(--accent)' : 'var(--text-3)'} fontSize={10} fontFamily="var(--mono)" fontWeight={hoveredIdx === i ? 600 : 400}>
          {d.month}
        </text>
      ))}
      {/* Value labels on Y axis */}
      {[0, 0.5, 1].map(pct => {
        const val = min + pct * range
        const y = py + (1 - pct) * (h - py * 2 - 20)
        return (
          <text key={pct} x={px - 6} y={y + 3} textAnchor="end" fill="var(--text-3)" fontSize={9} fontFamily="var(--mono)">
            ${Math.round(val / 1000)}k
          </text>
        )
      })}
    </svg>
  )
}
